import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { CssBaseline, ThemeProvider } from '@mui/material';

import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { ErrorBoundary } from 'react-error-boundary';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';

import { AuthProvider, useAuth } from '@/context/AuthContext';
import { NotificationProvider } from '@/context/NotificationContext';
import { ShortcutProvider } from '@/context/ShortcutContext';
import { UserDirectoryProvider } from '@/context/UserDirectoryContext';

import { loginRequest } from '@/auth/msalConfig';
import { lightTheme, darkTheme } from '@/theme';
import { ROUTES } from '@/constants/routes';
import { CONFIG } from '@/constants/config';

import { ErrorFallback } from '@/components/common/ErrorFallback';
import { LoadingScreen } from '@/components/common/LoadingScreen';
import { AccessDenied } from '@/components/common/AccessDenied';
import { Layout } from '@/components/Layout';

// Lazy-loaded pages
const PmDashboardPage = React.lazy(() => import('@/pages/PmDashboardPage'));
const TimesheetPage = React.lazy(() => import('@/pages/TimesheetPage'));
const SubmitsPage = React.lazy(() => import('@/pages/SubmitsPage'));
const ApprovalsPage = React.lazy(() => import('@/pages/ApprovalsPage'));
const ReportsHubPage = React.lazy(() => import('@/pages/ReportsHubPage'));
const SummaryReportPage = React.lazy(() => import('@/pages/SummaryReportPage'));
const TimeAuditPage = React.lazy(() => import('@/pages/TimeAuditPage'));
const ProjectsPage = React.lazy(() => import('@/pages/ProjectsPage'));
const ActivitiesPage = React.lazy(() => import('@/pages/ActivitiesPage'));
const ResourcesPage = React.lazy(() => import('@/pages/ResourcesPage'));
const PermissionsPage = React.lazy(() => import('@/pages/PermissionsPage'));
const WorkingHoursPage = React.lazy(() => import('@/pages/WorkingHoursPage'));
const DelegationsPage = React.lazy(() => import('@/pages/DelegationsPage'));
const TimeOffPage = React.lazy(() => import('@/pages/TimeOffPage'));
const AnalyticsPage = React.lazy(() => import('@/pages/AnalyticsPage'));
const MyProjectsPage = React.lazy(() => import('@/pages/MyProjectsPage'));
const ProfilePage = React.lazy(() => import('@/pages/ProfilePage'));

/** The old month calendar link opens the Timesheet Month tab on the same month (the page validates the values). */
const CalendarMonthRedirect: React.FC = () => {
  const { year, month } = useParams();
  const query = new URLSearchParams({ tab: 'month' });
  if (year) query.set('year', year);
  if (month) query.set('month', month);
  return <Navigate to={`${ROUTES.TIMESHEET}?${query.toString()}`} replace />;
};

interface ProtectedRoutesProps {
  isDarkMode: boolean;
  onToggleTheme: () => void;
}

const ProtectedRoutes: React.FC<ProtectedRoutesProps> = ({ isDarkMode, onToggleTheme }) => {
  const { user, isLoading, error, logout } = useAuth();

  if (isLoading) {
    return <LoadingScreen message="Loading user information..." />;
  }

  if (error || !user) {
    return <AccessDenied message={error ?? undefined} onLogout={logout} />;
  }

  const isPmOrAdmin = user.isProjectManager || user.isAdmin;

  return (
    <Routes>
        <Route element={<Layout isDarkMode={isDarkMode} onToggleTheme={onToggleTheme} />}>
          {/* Root redirect */}
          <Route index element={<Navigate to={ROUTES.TIMESHEET} replace />} />

          {/* My Work */}
          <Route path={ROUTES.TIMESHEET} element={<TimesheetPage />} />
          <Route path={ROUTES.CALENDAR} element={<Navigate to={ROUTES.TIMESHEET} replace />} />
          <Route path={ROUTES.CALENDAR_MONTH} element={<CalendarMonthRedirect />} />
          <Route path={ROUTES.MY_SUBMITS} element={<SubmitsPage />} />
          <Route path={ROUTES.TIME_OFF} element={<TimeOffPage />} />
          <Route path={ROUTES.ANALYTICS} element={<AnalyticsPage />} />
          <Route path={ROUTES.MY_PROJECTS} element={<MyProjectsPage />} />
          <Route path={ROUTES.PROFILE} element={<ProfilePage />} />

          {/* Reports (all authenticated users) */}
          <Route path={ROUTES.REPORTS.HUB} element={<ReportsHubPage />} />
          <Route path={ROUTES.REPORTS.SUMMARY} element={<SummaryReportPage />} />

          {/* Manager routes (PM or Admin) */}
          {isPmOrAdmin && (
            <>
              <Route path={ROUTES.DASHBOARD_PM} element={<PmDashboardPage />} />
              <Route path={ROUTES.APPROVALS} element={<ApprovalsPage />} />
              <Route path={ROUTES.REPORTS.TIME_AUDIT} element={<TimeAuditPage />} />
              <Route path={ROUTES.SETTINGS.PROJECTS} element={<ProjectsPage />} />
            </>
          )}

          {/* Admin routes */}
          {user.isAdmin && (
            <>
              <Route path={ROUTES.SETTINGS.ACTIVITIES} element={<ActivitiesPage />} />
              <Route path={ROUTES.SETTINGS.RESOURCES} element={<ResourcesPage />} />
              <Route path={ROUTES.SETTINGS.PERMISSIONS} element={<PermissionsPage />} />
              <Route path={ROUTES.SETTINGS.WORKING_HOURS} element={<WorkingHoursPage />} />
              <Route path={ROUTES.SETTINGS.DELEGATIONS} element={<DelegationsPage />} />
            </>
          )}

          {/* Fallback */}
          <Route path="*" element={<Navigate to={ROUTES.TIMESHEET} replace />} />
        </Route>
      </Routes>
  );
};

interface AppContentProps {
  isDarkMode: boolean;
  onToggleTheme: () => void;
}

const AppContent: React.FC<AppContentProps> = ({ isDarkMode, onToggleTheme }) => {
  const { instance, inProgress, accounts } = useMsal();

  useEffect(() => {
    if (inProgress === InteractionStatus.None && accounts.length === 0) {
      instance.loginRedirect(loginRequest).catch((error: unknown) => {
        console.error('==[Auth] Login redirect failed:', error);
      });
    }
  }, [inProgress, accounts, instance]);

  if (inProgress !== InteractionStatus.None) {
    return <LoadingScreen message="Authenticating..." />;
  }

  if (accounts.length === 0) {
    return <LoadingScreen message="Redirecting to login..." />;
  }

  return (
    <AuthProvider>
      <UserDirectoryProvider>
        <NotificationProvider>
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <BrowserRouter>
              <ShortcutProvider>
                  <ProtectedRoutes isDarkMode={isDarkMode} onToggleTheme={onToggleTheme} />
              </ShortcutProvider>
            </BrowserRouter>
          </ErrorBoundary>
        </NotificationProvider>
      </UserDirectoryProvider>
    </AuthProvider>
  );
};

function getInitialDarkMode(): boolean {
  const stored = localStorage.getItem(CONFIG.DARK_MODE_STORAGE_KEY);
  if (stored !== null) return stored === 'true';
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

const App: React.FC = () => {
  const [isDarkMode, setIsDarkMode] = useState(getInitialDarkMode);
  const theme = useMemo(() => (isDarkMode ? darkTheme : lightTheme), [isDarkMode]);

  useEffect(() => {
    document.documentElement.dataset.theme = isDarkMode ? 'dark' : 'light';
  }, [isDarkMode]);

  const handleToggleTheme = useCallback(() => {
    setIsDarkMode(prev => {
      const next = !prev;
      localStorage.setItem(CONFIG.DARK_MODE_STORAGE_KEY, String(next));
      return next;
    });
  }, []);

  // Listen for system theme changes
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent): void => {
      // Only follow system if user hasn't explicitly set a preference
      if (localStorage.getItem(CONFIG.DARK_MODE_STORAGE_KEY) === null) {
        setIsDarkMode(e.matches);
      }
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppContent isDarkMode={isDarkMode} onToggleTheme={handleToggleTheme} />
    </ThemeProvider>
  );
};

export default App;
